<template>
  <!-- ============================================================ -->
  <!-- 功能页 — 左侧导航 + 右侧内容区 + 锚点联动 + 底部营销文案     -->
  <!-- 数据合并自 Feature.vue 与 feature_1.vue 蓝湖设计稿            -->
  <!-- ============================================================ -->
  <div class="feat">
    <!-- ==================== 顶部标题区 ==================== -->
    <header class="feat__header">
      <h1 class="feat__title">{{ $t('80+功能模块，一站式覆盖外贸独立站所有需求') }}</h1>
      <p class="feat__subtitle">{{ $t('从建站到运营，从收款到物流，从营销到数据，Taoify 提供你需要的所有工具') }}</p>

      <!-- 搜索框 -->
      <div class="feat__search">
        <input
          v-model="searchQuery"
          class="feat__search-input"
          :placeholder="$t('搜索功能')"
          @keyup.enter="handleSearch"
        />
        <span class="feat__search-icon" @click="handleSearch">🔍</span>
      </div>
    </header>

    <!-- ==================== 主体：左侧导航 + 右侧内容 ==================== -->
    <div class="feat__body">
      <!-- ---------- 左侧导航 ---------- -->
      <nav class="feat-nav">
        <ul class="feat-nav__list">
          <li
            v-for="cat in categories"
            :key="cat.id"
            :class="[
              'feat-nav__item',
              { 'feat-nav__item--active': activeCategory === cat.id }
            ]"
            @click="scrollToCategory(cat.id)"
          >
            {{ $t(cat.name) }}
          </li>
        </ul>
      </nav>

      <!-- ---------- 右侧可滚动内容区 ---------- -->
      <div
        ref="contentRef"
        class="feat-main"
        @scroll="onContentScroll"
      >
        <section
          v-for="(cat, catIdx) in categories"
          :key="cat.id"
          :ref="cat.id"
          :id="cat.id"
          class="feat-section"
        >
          <h2 class="feat-section__title">{{ $t(cat.name) }}</h2>
          <div class="feat-section__grid">
            <div
              v-for="(feat, featIdx) in cat.features"
              :key="feat.title"
              class="feat-card"
            >
              <div class="feat-card__icon">
                <img
                  v-if="getFeatureIcon(catIdx, featIdx)"
                  class="feat-card__icon-img"
                  :src="getFeatureIcon(catIdx, featIdx)"
                  alt=""
                />
                <div v-else class="feat-card__icon-ph"></div>
              </div>
              <div class="feat-card__body">
                <h3 class="feat-card__title">{{ $t(feat.title) }}</h3>
                <p class="feat-card__desc">{{ $t(feat.description) }}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- ========== 底部营销文案（淡入过渡 + 顶部渐变遮边） ========== -->
    <transition name="feat-cta-fade">
      <div v-if="showMarketing" class="feat-cta">
        <p class="feat-cta__text">{{ $t('立即开启你的品牌独立站') }}</p>
        <p class="feat-cta__sub">{{ $t('免费试用 7 天，无需信用卡') }}</p>
        <button class="feat-cta__btn" @click="$router.push('/sign-up')">
          {{ $t('一键部署全球生意、立即免费试用') }}
        </button>
      </div>
    </transition>

    <!-- ========== 移动端返回顶部悬浮按钮 ========== -->
    <button
      v-show="showBackTop"
      class="feat-backtop"
      @click="scrollToTop"
      title="Back to top"
    >
      <span class="feat-backtop__arrow"></span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'Feature',

  data() {
    return {
      activeCategory: 'web-design',
      showMarketing: false,
      scrollTicking: false,
      /** 点击导航跳转中 — 抑制滚动侦听避免中间项闪烁 */
      isClickScrolling: false,
      searchQuery: '',
      /** 移动端返回顶部按钮显隐 */
      showBackTop: false,

      /* 10 大功能分类与特性 — 数据源自 Feature.vue + feature_1.vue 蓝湖设计稿合并 */
      /** 功能图标缓存：key = "cate{X}-{Y}", value = require() 路径 */
      _iconCache: {},

      categories: [
        {
          id: 'web-design',
          name: '网页设计与前端',
          features: [
            { title: '独立多语言版本', description: '支持英/日/俄/法/西/葡/德/意/阿拉伯语及简繁体中文等，每种语言可独立 SEO 收录。' },
            { title: '多货币切换', description: '实时汇率转换，默认币种可任意设置，提升跨境购买体验。' },
            { title: '50+ 风格模板', description: '覆盖服饰、电子、家居、美妆等多行业，一键切换，无需代码。' },
            { title: '响应式终端适配', description: '移动端、平板、PC 自动适配，完美迎合全球不同设备访问习惯。' },
            { title: '全球 CDN + SSL 证书', description: '全球节点加速 + 免费 SSL，网站打开更快，谷歌排名更友好。' },
            { title: '可视化首页编辑', description: '拖拽式操作，实时预览，不懂技术也能快速搭建专业品牌官网。' },
            { title: '多平台产品导入', description: '一键从速卖通、Shopify、1688、Xshoppy 导入产品，快速铺货。' },
          ],
        },
        {
          id: 'product-management',
          name: '产品管理',
          features: [
            { title: '零售与小额批发模式', description: '支持产品多规格（SKU）、阶梯批发价，满足B2C和B2B混合销售。' },
            { title: '批量上传与编辑', description: 'Excel 批量上传产品、图片、库存、价格，大幅提升上架效率。' },
            { title: 'AI 一键翻译商品', description: '批量或单个翻译产品标题、描述、图片文案，轻松进入多语言市场。' },
            { title: '自动 SEO 生成', description: '对接 AI 自动生成产品 SEO 标题、描述、关键词，提高收录机会。' },
            { title: '产品评论与买家秀', description: '支持会员/游客评论、图片上传、审核机制，提升社交信任感。' },
          ],
        },
        {
          id: 'marketing',
          name: '营销与促销',
          features: [
            { title: '优惠券系统', description: '支持现金券/折扣券，可按会员等级、消费金额设定使用条件。' },
            { title: '满减/满折活动', description: '满额减钱、满件打折、组合购买，有效提升客单价。' },
            { title: '限时促销倒计时', description: '给商品设置活动时限，增加买家紧迫感，提高转化率。' },
            { title: '弹窗广告', description: '快速推送新品、优惠券或订阅窗口，精准捕获潜在客户。' },
            { title: '分销系统（三级分销）', description: '会员分享推广链接，三级佣金分成，引爆社交裂变。' },
            { title: 'COD 单页落地页', description: '针对东南亚、中东等市场，一键生成高转化货到付款单页。' },
            { title: '购物车弃购挽回', description: '自动发送邮件提醒未完成订单的客户，挽回流失订单。' },
            { title: 'Google Shopping + Facebook Pixel', description: '自动同步商品至 Google Feed，完整对接 Pixel 和转化事件，助力广告投放。' },
          ],
        },
        {
          id: 'order-logistics',
          name: '订单与物流',
          features: [
            { title: '订单批量处理', description: '批量发货、批量导出快递单号，一键打印面单。' },
            { title: '智能物流跟踪', description: '对接全球 1000+ 物流公司（DHL、UPS、FedEx、USPS 等），客户实时查件。' },
            { title: '运费模板', description: '按重量、件数、区域自由设置运费，支持不同商品绑定不同物流方式。' },
            { title: '运单拆分', description: '针对百货类订单，支持一个订单拆分为多个包裹发货。' },
            { title: '库存预警', description: '批量设置库存阈值，低库存自动提醒，防止超卖。' },
            { title: '发货邮件通知', description: '系统自动发送发货通知邮件，提升客户体验。' },
          ],
        },
        {
          id: 'payment-currency',
          name: '支付与货币',
          features: [
            { title: 'PayPal 快捷支付', description: '支持个人/企业 PayPal，智能支付与快捷支付集成。' },
            { title: '信用卡通道', description: '对接 Stripe、2Checkout 等主流信用卡支付网关。' },
            { title: '本地化支付', description: '集成 Payssion、AsiaBill 等，支持东南亚、欧洲、拉美本地钱包和网银。' },
            { title: 'COD（货到付款）', description: '一键开启货到付款模式，适配新兴市场消费习惯。' },
            { title: '多币种结算', description: '前台展示多种货币，后台统一按基准币种结算，汇率自动更新。' },
          ],
        },
        {
          id: 'customer-membership',
          name: '客户与会员',
          features: [
            { title: '会员等级体系', description: '可自定义会员等级（铜牌/银牌/金牌），不同等级享受不同折扣权益。' },
            { title: '第三方社交登录', description: '支持 Facebook、Google、PayPal、VK 一键登录，降低注册门槛。' },
            { title: '会员站内信', description: '买家与卖家之间站内即时沟通，记录沟通历史。' },
            { title: '访客交互与实时查看', description: '后台实时查看在线访客、浏览页面、停留时长，主动发起聊天。' },
            { title: '邮箱验证', description: '注册邮件验证，有效防止虚假注册。' },
          ],
        },
        {
          id: 'data-analytics',
          name: '数据统计与分析',
          features: [
            { title: '流量统计', description: '来源渠道、国家分布、设备类型、关键词分析，全面了解客户来源。' },
            { title: '转化漏斗', description: '从访客→加购→下单→支付的全流程转化率，快速定位流失环节。' },
            { title: '产品销售排行', description: '按销量、销售额、收藏数统计，帮助选品和推广决策。' },
            { title: '顾客画像', description: '区分新客、复购客、沉睡客，支持 RFM 模型分析。' },
            { title: '实时视图', description: '当前在线人数、实时订单、实时销售额，随时掌握店铺动态。' },
          ],
        },
        {
          id: 'seo-tools',
          name: 'SEO 与推广工具',
          features: [
            { title: '站点地图自动生成', description: 'XML Sitemap 自动更新并提交搜索引擎。' },
            { title: '自定义 TDK', description: '每个商品、分类、页面可独立设置标题、描述、关键词。' },
            { title: '图片 Alt 优化', description: '自动或手动添加图片 Alt 属性，提升图片搜索排名。' },
            { title: '面包屑导航', description: '清晰的内链结构，同时改善用户体验和搜索引擎抓取。' },
            { title: 'Robots.txt 管理', description: '灵活控制搜索引擎抓取范围，优化索引效果。' },
            { title: '多语言 SEO 独立收录', description: '每种语言版本生成独立 URL，符合谷歌多语言站点规范。' },
          ],
        },
        {
          id: 'integration',
          name: '扩展与对接',
          features: [
            { title: 'ERP 系统对接', description: '已对接店小秘、易仓、马帮、通途、旺销王等主流 ERP，订单/库存自动同步。' },
            { title: 'Facebook 店铺', description: '一键搭建 Facebook 店铺，同步商品，支持站内直接下单。' },
            { title: 'ShareASale 联盟营销', description: '对接 ShareASale 广告联盟，拓展流量渠道。' },
            { title: 'EDM 邮件营销', description: '内置邮件订阅组件，支持批量发送营销邮件和自动化触达。' },
            { title: '公告生成器', description: '在网站顶部/底部跑马灯展示活动公告或物流通知。' },
            { title: '产品下载', description: '一键打包商品图片和信息，供分销商或第三方渠道使用。' },
          ],
        },
        {
          id: 'management-service',
          name: '管理与服务',
          features: [
            { title: '多角色权限管理', description: '可设置运营、客服、财务等不同后台账号权限，分工明确。' },
            { title: '多语言后台界面', description: '后台支持中英文等多语言，方便跨境团队协作。' },
            { title: '一对一专属顾问', description: '3 年以上经验顾问提供从建站到投放的全流程指导。' },
            { title: '在线培训课程', description: '80+ 节视频课程 + 每月大咖直播，覆盖建站、运营、广告、SEO。' },
            { title: '线下沙龙与资源对接', description: '定期举办线下交流活动，链接支付、物流、代运营等服务商。' },
            { title: '新店扶持服务', description: '提供 20+ 项新店上线扶持，包括 LOGO 制作、Banner 设计、产品上架指导等。' },
          ],
        },
      ],
    }
  },

  methods: {
    /** 点击左侧导航 → 立即选中 + 平滑滚动，中间项不闪烁 */
    scrollToCategory(catId) {
      // 立刻更新高亮，不等滚动完成
      this.activeCategory = catId
      // 标记为点击跳转中，抑制滚动侦听
      this.isClickScrolling = true

      const el = this.$refs[catId]
      if (el && el[0]) {
        el[0].scrollIntoView({ behavior: 'smooth', block: 'start' })
      }

      // 滚动动画结束后恢复侦听
      this._scheduleScrollReset()
    },

    /**
     * 根据分类/功能索引获取对应图标
     * 文件命名：cate{X}-{Y}@2x.png （X/Y 均从 1 开始）
     */
    getFeatureIcon(catIdx, featIdx) {
      const key = `cate${catIdx + 1}-${featIdx + 1}`
      return this._iconCache[key] || null
    },

    /** 返回顶部 */
    scrollToTop() {
      const container = this.$refs.contentRef
      if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth' })
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    /** 搜索功能：关键词匹配到功能模块后自动定位 */
    handleSearch() {
      const q = this.searchQuery.trim().toLowerCase()
      if (!q) return

      // 在所有分类中查找匹配的功能标题或描述
      for (const cat of this.categories) {
        for (const feat of cat.features) {
          if (
            feat.title.toLowerCase().includes(q) ||
            feat.description.toLowerCase().includes(q)
          ) {
            this.scrollToCategory(cat.id)
            return
          }
        }
      }
    },

    /** 滚动侦听（RAF 节流；点击跳转中跳过） */
    onContentScroll() {
      if (this.scrollTicking || this.isClickScrolling) return
      this.scrollTicking = true
      requestAnimationFrame(() => {
        this.trackActiveSection()
        this.scrollTicking = false
      })
    },

    /**
     * 根据滚动位置同步高亮左侧导航 + 控制底部营销文案显隐
     *
     * 高亮规则：取顶部已越过判定线（容器顶 + 80px）的最后一个分类；
     *           若已滚到容器底部，兜底强制选中最后一个分类。
     * 营销文案：最后一个分类区块与可视区有交集即显示。
     */
    trackActiveSection() {
      const container = this.$refs.contentRef
      if (!container) return

      const containerRect = container.getBoundingClientRect()
      const containerBottom = containerRect.bottom
      const threshold = containerRect.top + 80

      const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 5

      let currentId = this.categories[0].id
      const lastId = this.categories[this.categories.length - 1].id
      let lastVisible = false

      for (const cat of this.categories) {
        const el = this.$refs[cat.id]
        if (!el || !el[0]) continue
        const rect = el[0].getBoundingClientRect()

        if (rect.top <= threshold) currentId = cat.id

        if (cat.id === lastId) {
          lastVisible = rect.top < containerBottom && rect.bottom > containerRect.top
        }
      }

      if (atBottom) currentId = lastId

      this.activeCategory = currentId
      this.showMarketing = lastVisible
      // 返回顶部按钮：侦测容器或页面滚动位置
      this.showBackTop = (container.scrollTop || window.pageYOffset) > 300
    },

    /** 全局滚动侦听（移动端页面级滚动 + 桌面端容器滚动均覆盖） */
    onPageScroll() {
      this.showBackTop = (this.$refs.contentRef?.scrollTop || window.pageYOffset) > 300
    },

    /** 安排 isClickScrolling 复位（scrollend 事件 + 超时兜底） */
    _scheduleScrollReset() {
      const container = this.$refs.contentRef
      if (!container) {
        this.isClickScrolling = false
        return
      }

      const reset = () => {
        this.isClickScrolling = false
      }
      // 尝试使用 scrollend 事件（部分浏览器不支持，静默回退超时）
      try {
        const handler = () => {
          reset()
          container.removeEventListener('scrollend', handler)
        }
        container.addEventListener('scrollend', handler, { once: true })
      } catch (_) { /* ignore */ }

      // 兜底：800ms 后强制复位
      setTimeout(() => {
        if (this.isClickScrolling) this.isClickScrolling = false
      }, 800)
    },
  },

  created() {
    // 批量加载功能图标（在渲染前完成，避免模板访问 undefined）
    const iconCtx = require.context('@/assets/images/feature', false, /cate\d+-\d+@2x\.png/)
    this._iconCache = {}
    iconCtx.keys().forEach(k => {
      const name = k.replace('./', '').replace('@2x.png', '')
      this._iconCache[name] = iconCtx(k)
    })
  },

  mounted() {
    this.$nextTick(() => this.trackActiveSection())
    window.addEventListener('scroll', this.onPageScroll, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onPageScroll)
  },
}
</script>

<style scoped lang="scss">
/* ================================================================
   CSS 自定义属性 — 全局设计令牌（基于 1920px 设计稿换算为 px）
   ================================================================ */
.feat {
  --c-primary: #7331ff;
  --c-primary-light: rgba(115, 49, 255, 0.1);
  --c-text: #333333;
  --c-text-dark: #000000;
  --c-text-secondary: #666666;
  --c-placeholder: #c4c4c4;
  --c-bg: #f7f7f7;
  --c-white: #ffffff;
  --c-gradient-start: #f2ddff;
  --c-cta-start: #a692fd;
  --c-cta-end: #6355ff;
  --r-card: 16px;
  --r-nav: 20px;
  --r-nav-item: 8px;
  --r-search: 49px;
  --r-btn: 34px;
  --shadow-card: 0px 0px 13px rgba(0, 0, 0, 0.08);
  --shadow-search: 0px 0px 23px rgba(0, 0, 0, 0.08);
  --max-width: 1200px;

  background-color: var(--c-bg);
  font-family: 'Source Han Sans CN', 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

/* ================================================================
   顶部标题区（全宽背景 + 内容居中）
   ================================================================ */
.feat__header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 120px 0 80px;
  background: url('~@/assets/images/feature/feat__header.png') center no-repeat;
  background-size: cover;
}
.feat__title {
  color: var(--c-text);
  font-size: 48px;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
  max-width: 900px;
  margin: 0;
  padding: 0 20px;
}
.feat__subtitle {
  color: var(--c-primary);
  font-size: 24px;
  font-weight: 400;
  line-height: 1.4;
  letter-spacing: 0.5px;
  text-align: center;
  max-width: 800px;
  margin: 16px 0 0;
  padding: 0 20px;
}

/* ----- 搜索框 ----- */
.feat__search {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 560px;
  max-width: 90vw;
  height: 56px;
  background-color: var(--c-white);
  border-radius: var(--r-search);
  box-shadow: var(--shadow-search);
  margin-top: 40px;
  padding: 0 28px;
}
.feat__search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: var(--c-text);
  font-size: 16px;
  font-family: inherit;

  &::placeholder {
    color: var(--c-placeholder);
  }
}
.feat__search-icon {
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}

/* ================================================================
   主体区域：左侧导航 + 右侧内容（max-width 居中）
   ================================================================ */
.feat__body {
  position: relative;
  display: flex;
  gap: 24px;
  max-width: var(--max-width);
  margin: 12px auto 0;
  padding: 0 20px;

  /* 底部半透明渐变遮罩：衔接下方营销文案 */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 32px;
    background: linear-gradient(to bottom, transparent, var(--c-bg));
    pointer-events: none;
    z-index: 1;
  }
}

/* ================================================================
   左侧导航栏
   ================================================================ */
.feat-nav {
  flex-shrink: 0;
  width: 220px;
}
.feat-nav__list {
  list-style: none;
  margin: 0;
  padding: 0;
  background-color: var(--c-white);
  border-radius: var(--r-nav);
  overflow: hidden;
}
.feat-nav__item {
  font-size: 16px;
  color: var(--c-text-dark);
  cursor: pointer;
  padding: 12px 16px;
  margin: 6px 12px;
  line-height: 1.4;
  border-radius: var(--r-nav-item);
  font-weight: 400;
  transition: background-color 0.15s, color 0.15s;
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover,
  &--active {
    background-color: var(--c-primary-light);
    color: var(--c-primary);
    font-weight: 500;
  }
}

/* ================================================================
   右侧可滚动内容区
   ================================================================ */
.feat-main {
  flex: 1;
  height: 650px;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 3px; }
  &::-webkit-scrollbar-track { background: transparent; }
}

/* ================================================================
   功能分类区块
   ================================================================ */
.feat-section {
  &__title {
    font-size: 22px;
    font-weight: 500;
    color: var(--c-text-dark);
    margin: 0 0 20px;
    line-height: 1.3;
  }
  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
  }
}

/* ================================================================
   功能卡片
   ================================================================ */
.feat-card {
  display: flex;
  gap: 18px;
  background-color: var(--c-white);
  border-radius: var(--r-card);
  box-shadow: var(--shadow-card);
  padding: 36px 22px;
  min-height: 150px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.2s;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 35%;
    background-image: linear-gradient(180deg, var(--c-gradient-start) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.25s;
    pointer-events: none;
    border-radius: var(--r-card) var(--r-card) 0 0;
  }

  &:hover {
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.12);
  }
  &:hover::before {
    opacity: 1;
  }

  &__icon {
    flex-shrink: 0;
    width: 72px;
    height: 72px;
  }
  &__icon-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 8px;
  }
  &__icon-ph {
    width: 100%;
    height: 100%;
    background-color: rgba(115, 49, 255, 0.08);
    border-radius: 8px;
  }
  &__body {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
  }
  &__title {
    font-size: 18px;
    font-weight: 500;
    color: var(--c-text-dark);
    line-height: 1.3;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__desc {
    font-size: 14px;
    font-weight: 400;
    color: var(--c-text-secondary);
    line-height: 1.5;
    margin: 8px 0 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* ================================================================
   底部营销文案 — 淡入过渡 + 顶部渐变模糊遮边
   ================================================================ */
/* Vue transition: 淡入 + 微上移 */
.feat-cta-fade-enter-active,
.feat-cta-fade-leave-active {
  transition: opacity 0.45s ease, transform 0.45s ease;
}
.feat-cta-fade-enter,
.feat-cta-fade-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

.feat-cta {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 60px 20px 50px;
  background-color: var(--c-bg);

  /* 顶部渐变遮边：从透明过渡到背景色，弱化模块分割感 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 48px;
    background: linear-gradient(to bottom, transparent, var(--c-bg));
    pointer-events: none;
  }

  &__text {
    color: var(--c-text);
    font-size: 28px;
    font-weight: 500;
    line-height: 1.4;
    margin: 0 0 16px;
  }
  &__sub {
    color: var(--c-text-secondary);
    font-size: 18px;
    margin: 0 0 32px;
  }
  &__btn {
    display: inline-block;
    background-image: linear-gradient(360deg, #aa98fc 0%, var(--c-primary) 100%);
    box-shadow: 0px 4px 4px rgba(103, 91, 230, 0.2);
    color: var(--c-white);
    font-size: 16px;
    font-weight: 500;
    line-height: 1.5;
    border: none;
    border-radius: var(--r-btn);
    padding: 14px 48px;
    cursor: pointer;
    transition: opacity 0.15s;
    &:hover { opacity: 0.9; }
    &:active { opacity: 0.7; }
  }
}

/* ================================================================
   响应式：平板 (≤1024px) — 保持桌面布局，收紧间距
   ================================================================ */
@media (max-width: 1024px) {
  .feat__header {
    padding: 80px 0 60px;
  }
  .feat__title {
    font-size: 36px;
  }
  .feat__subtitle {
    font-size: 20px;
  }

  .feat__body {
    padding: 0 16px;
    gap: 16px;
  }
  .feat-nav {
    width: 180px;
  }
  .feat-nav__item {
    font-size: 14px;
    padding: 10px 14px;
    margin: 4px 10px;
  }

  .feat-main {
    height: 520px;
  }
  .feat-section__title {
    font-size: 20px;
  }
  .feat-card {
    padding: 28px 16px;
    min-height: 130px;
    gap: 14px;
  }
  .feat-card__icon {
    width: 56px;
    height: 56px;
  }
  .feat-card__title {
    font-size: 16px;
  }
  .feat-card__desc {
    font-size: 13px;
  }

  .feat-cta__text { font-size: 24px; }
  .feat-cta__sub { font-size: 16px; }
  .feat-cta__btn { font-size: 15px; padding: 12px 40px; }
}

/* ================================================================
   响应式：手机 (≤768px) — 纵向堆叠布局
   ================================================================ */
@media (max-width: 768px) {
  .feat__header {
    padding: 60px 16px 48px;
  }
  .feat__title {
    font-size: 28px;
    max-width: 100%;
    white-space: normal;
  }
  .feat__subtitle {
    font-size: 16px;
    max-width: 100%;
    white-space: normal;
    letter-spacing: 0;
    margin-top: 12px;
  }

  .feat__search {
    width: 100%;
    height: 48px;
    margin-top: 28px;
    border-radius: 24px;
    padding: 0 20px;
  }
  .feat__search-input { font-size: 15px; }
  .feat__search-icon { font-size: 18px; }

  .feat__body {
    flex-direction: column;
    gap: 20px;
    margin-top: 0;
    padding: 0 16px;
  }

  /* 导航：横向滚动标签 */
  .feat-nav {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 8px;
  }
  .feat-nav__list {
    display: flex;
    flex-direction: row;
    background: transparent;
    gap: 10px;
    padding: 4px 0 8px;
    overflow: visible;
  }
  .feat-nav__item {
    flex-shrink: 0;
    font-size: 14px;
    background: var(--c-white);
    border-radius: 8px;
    padding: 10px 16px;
    margin: 0;
    line-height: 1.4;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  }
  .feat-nav__item:hover,
  .feat-nav__item--active {
    margin: 0;
    padding: 10px 16px;
  }

  .feat-main {
    height: auto;
    overflow-y: visible;
  }
  .feat-section { margin-bottom: 32px; }
  .feat-section__title {
    font-size: 20px;
    margin-bottom: 16px;
  }
  .feat-section__grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .feat-card {
    padding: 20px;
    min-height: auto;
    gap: 14px;
    overflow: visible;
  }
  .feat-card__icon {
    width: 56px;
    height: 56px;
  }
  .feat-card__title {
    font-size: 16px;
    white-space: normal;
    overflow: visible;
  }
  .feat-card__desc {
    font-size: 14px;
    -webkit-line-clamp: unset;
    line-clamp: unset;
    display: block;
    overflow: visible;
    margin-top: 6px;
  }

  .feat-cta {
    padding: 48px 16px;
  }
  .feat-cta__text { font-size: 22px; margin-bottom: 12px; }
  .feat-cta__sub { font-size: 15px; margin-bottom: 24px; }
  .feat-cta__btn { font-size: 16px; padding: 14px 40px; border-radius: 28px; }

  /* 返回顶部 */
  .feat-backtop {
    position: fixed;
    right: 20px;
    bottom: 32px;
    z-index: 100;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: var(--c-primary);
    box-shadow: 0 4px 16px rgba(115, 49, 255, 0.35);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.3s, transform 0.3s;
    animation: feat-fade-in 0.3s ease;

    &:active { transform: scale(0.92); }
  }
  .feat-backtop__arrow {
    display: block;
    width: 14px;
    height: 14px;
    border-left: 2px solid #fff;
    border-top: 2px solid #fff;
    transform: rotate(45deg) translate(1px, 1px);
  }
}

@keyframes feat-fade-in {
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
}
</style>
